import {
  ICYDatum,
  ILoadable,
  LoadableStatus,
} from '../../Utils';
import {
  getCYHash,
  getGroupYHash,
  IPopulationMap,
} from './internalUtils';

const createMap = (inputStatus: ILoadable<ICYDatum[]>, groupsInputStatus, groupMembersInputStatus): ILoadable<IPopulationMap> => {

  let result: ILoadable<IPopulationMap>;
  if (inputStatus.status === LoadableStatus.Initial || groupsInputStatus.status === LoadableStatus.Initial || groupMembersInputStatus.status === LoadableStatus.Initial) {
    result = {status: LoadableStatus.Initial};
  } else if (inputStatus.status === LoadableStatus.Loading || groupsInputStatus.status === LoadableStatus.Loading || groupMembersInputStatus.status === LoadableStatus.Loading) {
    result = {status: LoadableStatus.Initial};
  } else if (inputStatus.status === LoadableStatus.NotPresent || groupsInputStatus.status === LoadableStatus.NotPresent || groupMembersInputStatus.status === LoadableStatus.NotPresent) {
    result = {status: LoadableStatus.Initial};
  } else {
    const {data: input} = inputStatus;
    const {data: groups} = groupsInputStatus;
    const {data: groupMembers} = groupMembersInputStatus;

    const dataByCountryYear: Map<string, number> = new Map();
    const dataByGroupYear: Map<string, number> = new Map();

    const availableYearsPerCountry: Map<number, Set<number>> = new Map();
    for (const {location_id, year, population} of input) {
      const key = getCYHash(location_id, year);
      dataByCountryYear.set(key, population);

      const retrievedAvailableYearsPerCountry = availableYearsPerCountry.get(location_id);
      let availableYears: Set<number>;
      if (retrievedAvailableYearsPerCountry === undefined) {
        availableYears = new Set();
        availableYearsPerCountry.set(location_id, availableYears);
      } else {
        availableYears = retrievedAvailableYearsPerCountry;
      }
      availableYears.add(year);
    }

    // Now do the same for regions and subregions
    const availableYearsPerGroup: Map<string, Set<number>> = new Map();

    const regions = [...groups.entries()].filter(g => g[1].group_type === "region");
    const subregions = [...groups.entries()].filter(g => g[1].group_type === "subregion");


    [regions, subregions].forEach(groupLevel => {
      groupLevel.forEach(group => {
        let groupId = group[0];
        let members = new Set();
        // Get member countries of this region
        [...groupMembers.entries()].filter(g => {
          let locationId = g[0];
          let memberships = g[1];
          let matchingMemberships = memberships.filter(m => m.group_id === groupId);
          if(matchingMemberships.length == 1) {
            members.add(locationId);
          } 
        });

        // Now, figure out available years for member countries
        let combinedAvailableYearsForGroup = new Set();
        [...members].forEach(member => {
          let memberAvailableYears = availableYearsPerCountry.get(member);

          for(let year of memberAvailableYears) {
            combinedAvailableYearsForGroup.add(year);
          }
        });

        let completeAvailableYearsForGroup = new Set();
        [...combinedAvailableYearsForGroup].forEach(year => {
          let memberPopulations = [...members].map(member => {
            return dataByCountryYear.get(`${member}-${year}`);
          });
          let totalPopulation = memberPopulations.reduce((totalPop, pop) => {
            return totalPop + pop;
          }, 0);

          if(!isNaN(totalPopulation)) {
            const groupKey = getGroupYHash(groupId, year);
            dataByGroupYear.set(groupKey, totalPopulation);  
            completeAvailableYearsForGroup.add(year);
          } else {
            // If the total summing returns NaN, there's 1+ country in the group
            // that doesn't have data for the year; remove this year for the group
            // as it is incomplete data
            // In other words, do nothing here to change `combinedAvailableYearsForGroup`
          }
        });

        // Add this group's combined available years
        availableYearsPerGroup.set(`group-${groupId}`, completeAvailableYearsForGroup);

      })
    });

    result = {
      status: LoadableStatus.Present,
      data: {
        availableYears: availableYearsPerCountry,
        dataByCountryYear,
        dataByGroupYear,
        availableYearsPerGroup
      },
    };

  }
  return result;
};

export default createMap;
